"use client";

import * as React from "react";
import { LogOut, Sun, Moon, Monitor } from "lucide-react";
import { cn } from "../lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";
import { useTheme } from "../theme/theme-provider";

export interface UserAccountMenuUser {
  name: string;
  email: string;
  avatar?: string;
  role?: string;
}

export interface UserAccountMenuProps {
  user: UserAccountMenuUser;
  onLogout: () => void;
  /** When true (sidebar collapsed), shows only the avatar with a tooltip */
  collapsed?: boolean;
  className?: string;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function UserAccountMenu({
  user,
  onLogout,
  collapsed = false,
  className,
}: UserAccountMenuProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        className={cn(
          "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
          collapsed && "justify-center px-0",
          className
        )}
      >
        <Avatar className="size-8 shrink-0">
          {user.avatar && <AvatarImage src={user.avatar} alt={user.name} />}
          <AvatarFallback className="text-xs">
            {getInitials(user.name)}
          </AvatarFallback>
        </Avatar>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium leading-tight">{user.name}</p>
            {user.role && (
              <Badge variant="secondary" className="mt-0.5 h-4 px-1.5 text-[10px] font-normal">
                {user.role}
              </Badge>
            )}
          </div>
        )}
      </button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <p>{user.name}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}

      <DropdownMenuContent
        side={collapsed ? "right" : "top"}
        align="start"
        sideOffset={8}
        className="w-56"
      >
        {/* User info header */}
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{user.name}</p>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Theme selector — icon-only with tooltips */}
        <div className="flex items-center justify-between px-2 pb-2 pt-1">
          <span className="text-xs text-muted-foreground">Theme</span>
          <div className="flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setTheme("system")}
                  className={cn(
                    "flex size-7 items-center justify-center rounded-md transition-colors",
                    theme === "system"
                      ? "bg-accent text-accent-foreground inset-ring-1 inset-ring-ring"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground"
                  )}
                >
                  <Monitor className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4}><p>System</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setTheme("light")}
                  className={cn(
                    "flex size-7 items-center justify-center rounded-md transition-colors",
                    theme === "light"
                      ? "bg-accent text-accent-foreground inset-ring-1 inset-ring-ring"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground"
                  )}
                >
                  <Sun className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4}><p>Light</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setTheme("dark")}
                  className={cn(
                    "flex size-7 items-center justify-center rounded-md transition-colors",
                    theme === "dark"
                      ? "bg-accent text-accent-foreground inset-ring-1 inset-ring-ring"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground"
                  )}
                >
                  <Moon className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4}><p>Dark</p></TooltipContent>
            </Tooltip>
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Sign out */}
        <DropdownMenuItem onClick={onLogout} className="cursor-pointer">
          <LogOut className="mr-2 size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
